import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Copy, Clock, Database, BarChart3, LineChart, PieChart, ScatterChart, AreaChart, MoreHorizontal, MoveRight, Loader2, FileImage } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ClientPlot } from '@/store/plots';
import { trackClient } from '@/lib/obs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';

const plotTypeIcons: Record<string, any> = {
  bar: BarChart3,
  line: LineChart,
  pie: PieChart,
  scatter: ScatterChart,
  area: AreaChart,
  default: BarChart3,
};

export interface PlotCardProps {
  plot: ClientPlot;
  datasetName?: string;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onRename?: (newTitle: string) => void;
  onDelete?: () => void;
  onMove?: (targetProjectId: string) => void;
  projectOptions?: Array<{ id: string; name: string }>;
  onDatasetClick?: () => void;
  isDuplicating?: boolean;
  onAddToFigure?: () => void;
  projectId?: string;
}

export function formatRelativeTime(date: Date | string | number): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return dateObj.toLocaleDateString();
}

export default function PlotCard({ plot, datasetName: _datasetName, onView, onEdit, onDuplicate, onRename, onDelete, onMove, projectOptions, onDatasetClick, isDuplicating, onAddToFigure, projectId }: PlotCardProps) {
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(plot.title || '');
  const renameCommittedRef = useRef(false);
  const { go } = useNav();

  // Default handler for "Add to Figure" if projectId is provided but onAddToFigure is not
  const handleAddToFigure = onAddToFigure || (projectId ? () => {
    go(routes.project.figures.new(projectId) + `?plotId=${plot.id}`);
  } : undefined);
  const typeFromSpec = (plot as any)?.plotSpec?.plotType;
  const typeFromTrace = (plot as any)?.plotlyJson?.data?.[0]?.type;
  const plotType: string = (typeFromSpec || typeFromTrace || 'bar').toLowerCase();
  const Icon = plotTypeIcons[plotType] || plotTypeIcons.default;
  const thumbUrl = (plot as any)?.thumb_url as string | undefined;
  const handleImgError = (e: any) => {
    try { e.currentTarget.style.display = 'none'; } catch {}
    try { trackClient('thumb_fallback', { plotId: plot.id }); } catch {}
  };
  const handleImgLoad = () => {
    try { trackClient('thumb_loaded', { plotId: plot.id }); } catch {}
  };

  // Lazy-load thumbnail using IntersectionObserver to avoid offscreen fetches
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [canLoad, setCanLoad] = useState(false);
  useEffect(() => {
    if (!thumbUrl) return;
    const el = imgRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setCanLoad(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e && e.isIntersecting) {
        setCanLoad(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [thumbUrl]);

  useEffect(() => {
    if (!renaming) {
      setNewTitle(plot.title || '');
    }
  }, [plot.title, renaming]);

  const initialTitle = (plot.title || '').trim();

  const beginRename = () => {
    renameCommittedRef.current = false;
    setNewTitle(plot.title || '');
    setRenaming(true);
  };

  const commitRename = () => {
    if (renameCommittedRef.current) return;
    renameCommittedRef.current = true;
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setNewTitle(plot.title || '');
      setRenaming(false);
      return;
    }
    if (onRename && trimmed !== initialTitle) {
      onRename(trimmed);
    }
    setRenaming(false);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="min-h-[48px] flex items-center">
              {renaming ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    commitRename();
                  }}
                  className="w-full"
                >
                  <input
                    className="text-sm border border-border rounded px-2 py-1 w-full"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setRenaming(false);
                        setNewTitle(plot.title || '');
                      }
                    }}
                    autoFocus
                  />
                </form>
              ) : (
                <h3
                  className="font-medium text-base line-clamp-2 cursor-text"
                  onClick={beginRename}
                  title="Click to rename"
                >
                  {plot.title || 'Untitled Plot'}
                </h3>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={beginRename}>Rename</DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate} disabled={!!isDuplicating}>
                {isDuplicating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Duplicating…
                  </span>
                ) : (
                  'Duplicate'
                )}
              </DropdownMenuItem>
              {handleAddToFigure && (
                <DropdownMenuItem onClick={handleAddToFigure}>
                  <FileImage className="h-3 w-3 mr-2" />
                  Add to Figure
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                  Delete
                </DropdownMenuItem>
              )}
              {onMove && projectOptions && projectOptions.length > 0 && (
                <div className="px-2 py-1.5">
                  <div className="text-xs text-muted-foreground mb-1">Move to project</div>
                  <div className="flex flex-col gap-1">
                    {projectOptions.map((p) => (
                      <button key={p.id} className="text-sm text-left hover:underline flex items-center gap-2" onClick={() => onMove(p.id)}>
                        <MoveRight className="h-3 w-3" /> {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent>
        <div className="aspect-video bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden" onClick={onView}>
          {thumbUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              ref={imgRef}
              src={canLoad ? thumbUrl : undefined}
              alt={plot.title || 'Plot thumbnail'}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={handleImgError}
              onLoad={handleImgLoad}
            />
          ) : (
            <Icon className="h-12 w-12 text-muted-foreground" />
          )}
        </div>

        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(plot.createdAt)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2"
            onClick={onDatasetClick}
            disabled={!onDatasetClick}
            title={'Data'}
          >
            <Database className="h-3 w-3 mr-1" />
            <span className="truncate">Data</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2"
            onClick={onEdit}
          >
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="flex items-center justify-center gap-2"
                  onClick={onDuplicate}
                  disabled={!!isDuplicating}
                  aria-busy={isDuplicating ? true : undefined}
                  title={isDuplicating ? 'Duplicating…' : undefined}
                >
                  {isDuplicating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                Duplicate this plot. Creates a new copy and opens it in this browser.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
